import CreditCardIcon from '@mui/icons-material/CreditCard';
import { Button, Grid, List, Paper, Typography } from '@mui/material';
import { SearchFilter } from 'components/ui/SearchFilter';
import { SingleOrderCompanyView } from 'pages/Orders/Components/SingleOrderCompanyView';
import ownerAtom from 'data/atoms/ownerAtom';
import userAtom from 'data/atoms/userAtom';
import useWindowsDimension from 'functions/useWindowsDimension';
import { useAtom } from 'jotai';
import { useEffect, useReducer, useState } from 'react';
import {
  initialRootOrderState,
  RootOrderReducer,
} from './Reducers/RootOrderReducer';
import { useGet, usePost } from 'http/useApi';
import { rq, rqPost } from 'http/apiRoutes';
import {
  OrderedOrderHeaderViewModel,
  OrderedPageViewModel,
  OrderedPurchaseLineViewModel,
  UpdateOrderReceiptItem,
  UpdateOrderReceiptModel,
} from './models/orderedModel';
import OrderedLeftSideItem from './ViewModels/OrderedLeftSideItem';
import { OrderedRightSideItem } from './ViewModels/OrderedRightSideItem';
import { useSnackbar } from 'notistack';
import { SingleOrderEmployeeView } from './Components/SingleOrderEmployeeView';
import { EnumOrderStatus } from 'Interfaces/IGlobalTypeConfig';

export const Ordered = () => {
  // ***************************************************************************
  // INITIALIZATION
  // ***************************************************************************
  const { height } = useWindowsDimension();
  const [state, dispatch] = useReducer(RootOrderReducer, initialRootOrderState);
  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(true);
  const [isReloadOrderedData, setIsReloadOrderedData] = useState<boolean>(true);
  const [owner] = useAtom(ownerAtom);
  const [user] = useAtom(userAtom);
  const { enqueueSnackbar } = useSnackbar();

  // ***************************************************************************
  // PAGINATION
  // ***************************************************************************
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(25);

  // ***************************************************************************
  // API CALLS
  // ***************************************************************************
  const isEnabled = isReloadOrderedData;
  const query = useGet(rq.getOrdered(page, pageSize, isEnabled), );

  // POST /api/orders/{orderId}/received
  const isChanged = !isButtonDisabled;
  const updateDelivered = usePost(rqPost.updateDelivered(), );

  // ***************************************************************************
  // HANDLERS
  // ***************************************************************************
  const onSelectOrder = (o: OrderedOrderHeaderViewModel) => {
    if (!o) {
      dispatch({
        type: 'ORDERED/SET_SELECTED_ORDER',
        payload: { order: null },
      });
      dispatch({
        type: 'ORDERED/SET_SELECTED_VENDOR',
        payload: { vendor: null },
      });
      dispatch({
        type: 'ORDERED/SET_SELECTED_VENDOR_CONTACT',
        payload: { vendorContact: null },
      });
      setIsButtonDisabled(true);
      return;
    }

    dispatch({
      type: 'ORDERED/SET_SELECTED_ORDER',
      payload: { order: o },
    });
    dispatch({
      type: 'ORDERED/SET_SELECTED_VENDOR',
      payload: { vendor: o.vendor ?? null },
    });
    dispatch({
      type: 'ORDERED/SET_SELECTED_VENDOR_CONTACT',
      payload: { vendorContact: o.vendorContact ?? null },
    });
    setIsButtonDisabled(true);
  };

  /**
   * Registrer endring i LEVERT antall på en line.
   * Clamp: 0..orderedQuantity (delta mottatt nå).
   */
  const handleChangeDelivered = (
    line: OrderedPurchaseLineViewModel,
    value: string
  ) => {
    if (!state.ordered.selectedOrder) return;

    const maxDeliverable = Number(line.orderedQuantity ?? 0);
    const raw = Number.parseInt((value ?? '').toString(), 10);
    const safe = Number.isFinite(raw)
      ? Math.max(0, Math.min(raw, maxDeliverable))
      : 0;

    dispatch({
      type: 'ORDERED/UPDATE_LINE_RECEIVED_QUANTITY',
      payload: {
        orderPurchaseLineId: line.orderPurchaseLineId,
        receivedQuantity: safe,
      },
    });

    setIsButtonDisabled(false);
  };

  const handleChange = (
    field: 'trackingNo' | 'vendorOrderNumber',
    value: string
  ) => {
    if (!state.ordered.selectedOrder) return;
    const updatedOrder: OrderedOrderHeaderViewModel = {
      ...state.ordered.selectedOrder,
      [field]: value,
    };
    dispatch({
      type: 'ORDERED/SET_SELECTED_ORDER',
      payload: { order: updatedOrder },
    });
    dispatch({
      type: 'ORDERED/UPDATE_ORDER_LIST',
      payload: { order: updatedOrder },
    });
    setIsButtonDisabled(false);
  };

  // ***************************************************************************
  // EFFECTS
  // ***************************************************************************
  useEffect(() => {
    if (query.isSuccess && query.data) {
      const page = query.data as OrderedPageViewModel;
      console.log('Ordered page data', page);

      dispatch({ type: 'ORDERED/LOAD_PAGE', payload: page });

      dispatch({
        type: 'ORDERED/SET_SELECTED_VENDOR',
        payload: { vendor: page.orders[0]?.vendor ?? null },
      });
      dispatch({
        type: 'ORDERED/SET_SELECTED_VENDOR_CONTACT',
        payload: { vendorContact: page.orders[0]?.vendorContact ?? null },
      });

      setIsReloadOrderedData(false);
    }
  }, [query.isSuccess, query.data]);

  // ***************************************************************************
  // BYGG RECEIPT-PAYLOAD
  // ***************************************************************************
  const buildReceiptPayload = (
    sel: OrderedOrderHeaderViewModel
  ): UpdateOrderReceiptModel => {
    const items: UpdateOrderReceiptItem[] = (sel.lines ?? []).map((l) => ({
      orderPurchaseLineId: l.orderPurchaseLineId, // ⬅️ line-id
      deliveredQuantity: Number(l.receivedQuantity ?? 0), // send verdien du viser i UI
    }));

    return {
      orderId: sel.orderId,
      trackingNo: sel.trackingNo ?? '',
      vendorOrderNumber: sel.vendorOrderNumber ?? '',
      items,
    };
  };

  // ***************************************************************************
  // ON SUBMIT
  // ***************************************************************************
  const onSubmit = async () => {
    if (!owner) return;
    if (!user) return;

    const sel = state.ordered.selectedOrder;
    if (!sel) return;

    // 1) Mottak (delivered)
    const receiptPayload = buildReceiptPayload(sel);

    try {
      if (receiptPayload.items.length > 0) {
        await updateDelivered.mutateAsync(receiptPayload);
      }

      enqueueSnackbar('Ordre oppdatert', { variant: 'success' });
      setIsButtonDisabled(true);
      // Hvis du vil refetche etterpå:
      // setIsReloadOrderedData(true);
    } catch (err) {
      console.error('onSubmit error', err);
      enqueueSnackbar('Kunne ikke oppdatere ordre', { variant: 'error' });
    }
  };

  // ***************************************************************************
  // RENDERING
  // ***************************************************************************
  return (
    <Grid container spacing={0} sx={{ display: 'flex', flexDirection: 'row' }}>
      {/* LEFT SIDE */}
      <Grid item xs={6} sx={{ pr: 0.5, m: 0 }}>
        <SearchFilter Icon={CreditCardIcon} Header='orders' />
        <List
          sx={{
            p: 2,
            height: Math.ceil(height - 242),
            backgroundColor: 'primary.back',
            overflow: 'auto',
          }}
        >
          {state.ordered.orders.map(
            (o) =>
              o.orderStatus !== EnumOrderStatus.Delivered && (
                <OrderedLeftSideItem
                  key={o.orderId}
                  order={o}
                  selectedOrderId={state.ordered.selectedOrder?.orderId ?? null}
                  onSelectOrder={() => onSelectOrder(o)}
                />
              )
          )}
        </List>
        <Paper
          variant='elevation3'
          sx={{
            p: 2,
            height: 64,
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
          }}
        ></Paper>
      </Grid>

      {/* RIGHT SIDE */}
      <Grid item xs={6} sx={{ pl: 0.5, m: 0 }}>
        <SearchFilter Icon={CreditCardIcon} Header='Selected order' />
        <Grid
          container
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexGrow: 1,
            p: 2,
            pb: 0,
            backgroundColor: 'primary.main',
          }}
        >
          <Grid item xs={7}>
            {state.ordered.selectedVendor && (
              <SingleOrderCompanyView company={state.ordered.selectedVendor} />
            )}
          </Grid>
          <Grid item xs={5}>
            {state.ordered.selectedVendorContact && (
              <SingleOrderEmployeeView
                employee={state.ordered.selectedVendorContact}
              />
            )}
          </Grid>
          <Grid
            item
            xs={12}
            sx={{
              m: 0,
              p: 0,
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
            }}
          >
            <Typography sx={{ p: 2, fontWeight: 'bold', width: '25%' }}>
              Shipping Cost:
            </Typography>
            <Typography
              sx={{
                p: 2,
                width: '25%',
                fontWeight: 'bold',
                textAlign: 'right',
              }}
            >
              {'0.00'}
            </Typography>
            <Typography sx={{ p: 2, fontWeight: 'bold', width: '25%' }}>
              Total Cost:
            </Typography>
            <Typography
              sx={{
                p: 2,
                width: '25%',
                fontWeight: 'bold',
                textAlign: 'right',
              }}
            >
              {'0.00'}
            </Typography>
          </Grid>
        </Grid>
        <List
          sx={{
            p: 2,
            height: Math.ceil(height - 410),
            backgroundColor: 'primary.back',
            overflow: 'auto',
          }}
        >
          <OrderedRightSideItem
            order={state.ordered.selectedOrder}
            loading={false}
            handleChange={handleChange}
            handleChangeDelivered={handleChangeDelivered}
          />
        </List>
        <Paper
          variant='elevation3'
          sx={{
            p: 2,
            height: 64,
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
          }}
        >
          <Grid
            item
            xs={12}
            sx={{
              p: 0,
              m: 0,
              mb: 2,
              display: 'flex',
              flexDirection: 'row-reverse',
            }}
          >
            <Button
              variant='contained'
              onClick={onSubmit}
              disabled={isButtonDisabled}
            >
              Update Order
            </Button>
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  );
};
